import $axios from '@/backend.js'
import { sortArray, isEditorRole, isObserverRole, isDistributedInstance, isMasterInstance, isViewerRole } from '@/utils'
import { getInstance } from '@/auth/auth'
import { on as onPushEvent, onConnected as onPushConnected, onPushFallback, PushEventKind } from '@/push'

const getDefaultState = () => {
    return {
        instanceSnapshots: [],
        instanceUsers: [],
        instanceInvitations: [],
        fetchingInstanceUsers: false,
        fetchingInstanceInvitations: false,
        canSelectAndDistributeSnapshot: false,
        fetchingSnapshots: false,
        snapshotPollingInterval: null, // poll-fallback timer id, active only while push is down past grace
        snapshotPushUnsubs: null, // [unsub, ...] for task_finished / connected
        fetchingInstanceUsage: false,
        instanceUsage: null
    }
}
const instanceStore = {
    namespaced: true,
    state: getDefaultState(),
    mutations: {
        resetInstanceState(state) {
            if (state.snapshotPollingInterval) {
                clearInterval(state.snapshotPollingInterval)
            }
            if (state.snapshotPushUnsubs) {
                for (const unsub of state.snapshotPushUnsubs) {
                    try {
                        unsub()
                    } catch (e) {
                        /* ignore */
                    }
                }
            }
            Object.assign(state, getDefaultState())
        },
        setSnapshotPollingInterval: function (state, interval) {
            state.snapshotPollingInterval = interval
        },
        setSnapshotPushUnsubs: function (state, unsubs) {
            state.snapshotPushUnsubs = unsubs
        },
        setSnapshots: function (state, snapshots) {
            state.instanceSnapshots = snapshots.filter(snapshot => snapshot.lock_mode_name !== 'DELETING')
        },
        setUsers: function (state, users) {
            state.instanceUsers = users
        },
        setInvitations(state, invitations) {
            state.instanceInvitations = invitations
        },
        setFetchingInstanceUsers(state, fetching) {
            state.fetchingInstanceUsers = fetching
        },
        setFetchingInstanceInvitations(state, fetching) {
            state.fetchingInstanceInvitations = fetching
        },
        setFetching: function (state, newVal) {
            state.fetchingSnapshots = newVal
        },
        setFetchingInstanceUsage(state, newVal) {
            state.fetchingInstanceUsage = newVal
        },
        setInstanceUsage(state, usage) {
            state.instanceUsage = usage
        }
    },
    actions: {
        resetInstanceState: function (context) {
            context.commit('resetInstanceState')
        },
        setSnapshotPollingInterval: function (context, iid) {
            // Snapshot list: task_finished + reconnect resync are primary.
            // Standing 5min poll arms only while push is down past grace
            // (temporary dual-compat with old BE / push outages).
            if (context.state.snapshotPushUnsubs) {
                for (const unsub of context.state.snapshotPushUnsubs) {
                    try {
                        unsub()
                    } catch (e) {
                        /* ignore */
                    }
                }
                context.commit('setSnapshotPushUnsubs', null)
            }
            if (context.state.snapshotPollingInterval) {
                clearInterval(context.state.snapshotPollingInterval)
                context.commit('setSnapshotPollingInterval', null)
            }
            const refresh = () => {
                if (getInstance().isAuthenticated) {
                    context.dispatch('fetchSnapshots', iid)
                }
            }
            const stopPoll = () => {
                if (context.state.snapshotPollingInterval) {
                    clearInterval(context.state.snapshotPollingInterval)
                    context.commit('setSnapshotPollingInterval', null)
                }
            }
            const startPoll = () => {
                stopPoll()
                refresh()
                context.commit(
                    'setSnapshotPollingInterval',
                    setInterval(() => {
                        refresh()
                    }, 300000)
                )
            }
            const unsubs = [
                onPushEvent(PushEventKind.TASK_FINISHED, refresh),
                onPushConnected(refresh),
                onPushFallback(active => {
                    if (active) startPoll()
                    else stopPoll()
                })
            ]
            context.commit('setSnapshotPushUnsubs', unsubs)
        },
        fetchSnapshots: function (context, iid) {
            return $axios
                .get(`instances/${iid}/snapshots`)
                .then(function (response) {
                    const sortByIdDesc = sortArray(response.data, 'snid', 'descending', false)
                    context.commit('setSnapshots', sortByIdDesc)
                })
                .catch(error => {
                    console.log(error)
                })
        },
        fetchInstanceSnapshots: function (context, iid) {
            context.commit('setFetching', true)
            return context.dispatch('fetchSnapshots', iid).finally(() => {
                context.commit('setFetching', false)
            })
        },
        fetchInstanceUsers: function (context, iid) {
            context.commit('setFetchingInstanceUsers', true)
            return $axios
                .get(`/instances/${iid}/list_users`)
                .then(response => {
                    context.commit('setUsers', response.data)
                })
                .catch(() => {
                    context.commit('setUsers', [])
                })
                .finally(() => {
                    context.commit('setFetchingInstanceUsers', false)
                })
        },
        fetchInstanceInvitations(context, iid) {
            context.commit('setFetchingInstanceInvitations', true)
            return $axios
                .post(`/instances/${iid}/invitations`, {})
                .then(response => {
                    context.commit('setInvitations', response.data)
                })
                .catch(() => {
                    context.commit('setInvitations', [])
                })
                .finally(() => {
                    context.commit('setFetchingInstanceInvitations', false)
                })
        },
        updateCreateAndInviteStep: function (context, newVal) {
            context.commit('updateCreateAndInviteStep', newVal)
        },
        resetCreateAndShareData: function (context) {
            context.commit('resetCreateAndShareData')
        },
        async fetchInstanceUsage(context, iid) {
            context.commit('setFetchingInstanceUsage', true)
            try {
                const { data } = await $axios.get(`/space_credit_limit/usage/${iid}`)
                context.commit('setInstanceUsage', data)
            } catch (error) {
                context.commit('setInstanceUsage', error)
            } finally {
                context.commit('setFetchingInstanceUsage', false)
            }
        }
    },
    getters: {
        currentInstanceName: (state, getters, rootState) => {
            if (rootState.route.params.iid !== undefined && rootState.spaceStore.spaceInstances.length) {
                const instance = rootState.spaceStore.spaceInstances.find(instance => instance.iid.toString() === rootState.route.params.iid.toString())
                if (instance) {
                    return instance.long_id
                }
            }
            return null
        },
        currentInstanceDescription: (state, getters, rootState) => {
            if (rootState.route.params.iid !== undefined && rootState.spaceStore.spaceInstances.length) {
                const instance = rootState.spaceStore.spaceInstances.find(instance => instance.iid.toString() === rootState.route.params.iid.toString())
                if (instance) {
                    return instance.description
                }
            }
            return 'No description available for this instance'
        },
        instanceLongNameById: (state, getters, rootState) => iid => {
            if (iid !== undefined && iid !== null && rootState.spaceStore.spaceInstances.length) {
                const instance = rootState.spaceStore.spaceInstances.find(instance => instance.iid.toString() === iid.toString())
                if (instance) {
                    return instance.long_id
                }
            }
            return null
        },
        instanceRearchiveAfterTimestamp: (state, getters, rootState) => iid => {
            if (iid !== undefined && iid !== null && rootState.spaceStore.spaceInstances.length) {
                const instance = rootState.spaceStore.spaceInstances.find(instance => instance.iid.toString() === iid.toString())
                if (instance) {
                    return instance.rearchive_after_timestamp
                }
            }
            return null
        },
        isInstanceEditor: (state, getters, rootState) => {
            if (rootState.route.params.iid !== undefined && rootState.spaceStore.spaceInstances.length) {
                const currentInstance = rootState.spaceStore.spaceInstances.find(instance => instance.iid.toString() === rootState.route.params.iid.toString())
                if (currentInstance && isEditorRole(currentInstance.role)) {
                    return true
                }
            }
            return false
        },
        isInstanceViewer: (state, getters, rootState) => {
            if (rootState.route.params.iid !== undefined && rootState.spaceStore.spaceInstances.length) {
                const currentInstance = rootState.spaceStore.spaceInstances.find(instance => instance.iid.toString() === rootState.route.params.iid.toString())
                if (currentInstance && isViewerRole(currentInstance.role)) {
                    return true
                }
            }
            return false
        },
        isInstanceArchived: (state, getters, rootState) => {
            if (rootState.route.params.iid !== undefined && rootState.spaceStore.spaceInstances.length) {
                const currentInstance = rootState.spaceStore.spaceInstances.find(instance => instance.iid.toString() === rootState.route.params.iid.toString())
                if (!currentInstance) return false
                return !currentInstance.filesystem_prefix
            }
            return false
        },
        isInstanceObserver: (state, getters, rootState) => {
            if (rootState.route.params.iid !== undefined && rootState.spaceStore.spaceInstances.length) {
                const currentInstance = rootState.spaceStore.spaceInstances.find(instance => instance.iid.toString() === rootState.route.params.iid.toString())
                if (currentInstance && isObserverRole(currentInstance.role)) {
                    return true
                }
            }
            return false
        },
        isDistributedInstance: (state, getters, rootState) => {
            if (rootState.route.params.iid && rootState.spaceStore.spaceInstances.length) {
                const distributedInstance = rootState.spaceStore.spaceInstances.find(
                    instance => instance.iid.toString() === rootState.route.params.iid.toString()
                )
                if (distributedInstance && isDistributedInstance(distributedInstance.long_id)) {
                    return true
                }
            }
            return false
        },
        isMasterInstance: (state, getters, rootState) => {
            if (rootState.route.params.iid && rootState.spaceStore.spaceInstances.length) {
                const masterInstance = rootState.spaceStore.spaceInstances.find(instance => instance.iid.toString() === rootState.route.params.iid.toString())
                if (masterInstance && isMasterInstance(masterInstance.long_id)) {
                    return true
                }
            }
            return false
        },
        canDeleteInstanceOrEditFields: (state, getters, rootState) => {
            if (rootState.route.params.iid && rootState.spaceStore.spaceInstances.length) {
                const undeletableInstance = ['distributed', 'master']
                const filteredInstance = rootState.spaceStore.spaceInstances.find(instance => instance.iid.toString() === rootState.route.params.iid.toString())
                if (filteredInstance && undeletableInstance.indexOf(filteredInstance.short_id) > -1) {
                    return false
                }
            }
            return false
        }
    }
}
export default instanceStore
